/**
 * Defines the API exported by the behavior plugin.
 * @module
 */
var _ = require('optimizely-client-fn');
var eventsHelper = require('../helpers/events');
var rule_builder = require('../helpers/rule_builder');
var rules = require('../helpers/rules');

/**
 * Generates rule from descriptor, runs rule on events, and returns results.
 * @param {BehaviorEvent[]} events
 * @param {RuleSpecV0_1} descriptor
 * @returns {Object[]}
 * @private
 */
function generateAndExecuteRule(events:import('core/behavior/event').Event[], descriptor:RuleSpecV0_1): any[] {
  var ruleDescription = rule_builder.buildFromSpecV0_1(descriptor);
  if (ruleDescription.length !== 1) {
    throw new Error('Invalid query descriptor; verify that no aggregators are specified');
  }
  return rules.execute(ruleDescription[0], events);
}

/**
 * Returns events as array of plain old objects.
 * @param {BehaviorEvent[]|Object[]} events
 * @param {Object} prefs
 * @param {boolean} prefs.revenueAsTag Whether revenue should be included as a tag, rather than as a top-level field.
 * @param {boolean} prefs.timeAsTimestamp Whether time should be returned as a Date object under the timestamp field,
 *                                        rather than as a number under the time field.
 * @returns {Object[]}
 * @private
 */
function eventsToObjects(events:import('core/behavior/event').Event[]|any[], prefs:any): any[] {
  return _.map(events, function(e) {
    if (_.isFunction(e.toObject)) {
      return e.toObject(prefs);
    }
    return e;
  });
}

/**
 * Validates that descriptor is allowed for supported API calls.
 * @param {RuleSpecV0_1} descriptor
 * @param {string[]} allowedPickModifiers Allowed pick modifier strings.
 * @returns {string[]|undefined} Validation errors.
 * @private
 */
function validateDescriptorForAPI(descriptor:RuleSpecV0_1, allowedPickModifiers:string[]): string[]|undefined {
  if (!descriptor) {
    return ['Descriptor not defined'];
  }

  var errors = [];
  if (descriptor.count) {
    errors.push('Unexpected "count" clause specified');
  }

  if (descriptor.pick && descriptor.pick.modifier && allowedPickModifiers.indexOf(descriptor.pick.modifier) === -1) {
    errors.push('Invalid "pick" modifier "' + descriptor.pick.modifier + '"');
  }

  _.each(descriptor.filters, function(filter) {
    if (!_.isUndefined(filter.modifier)) {
      errors.push('Unexpected "filter" modifier "' + filter.modifier + '"');
    }
  });

  if (errors.length > 0) {
    return errors;
  }
}

/**
 * Returns events based on specifications in descriptor.
 *  - if descriptor is undefined, return all events;
 *  - if descriptor is a number n, return the most recent n events;
 *  - if descriptor is a rule, return result of running rule on all events.
 *
 * @param {Store} VisitorEventsStore
 * @param {Number|RuleSpecV0_1} [descriptor]
 * @returns {Object[]}
 * @private
 */
function getEvents(VisitorEventsStore:StoreClass, descriptor?:number|RuleSpecV0_1): any[] {
  var eventObjectPrefs = {
    revenueAsTag: false,
    timeAsTimestamp: true,
  };

  var events;
  if (_.isUndefined(descriptor)) {
    events = eventsHelper.getEvents(VisitorEventsStore);
    return eventsToObjects(events, eventObjectPrefs);
  } else if (_.isNumber(descriptor)) {
    if (descriptor <= 0) {
      throw new Error('Count must be a positive integer, got ' + descriptor);
    }
    // Return most recent events.
    events = eventsHelper.getEvents(VisitorEventsStore);
    return eventsToObjects(events.slice(-descriptor), eventObjectPrefs);
  } else {
    // Assert that descriptor is valid.
    var errors = validateDescriptorForAPI(<RuleSpecV0_1>descriptor, _.values(rule_builder.RECENCY_FILTERS));
    if (errors) {
      throw new Error(errors.join('\n'));
    }
    events = eventsHelper.getEvents(VisitorEventsStore);
    return eventsToObjects(generateAndExecuteRule(events, <RuleSpecV0_1>descriptor), eventObjectPrefs);
  }
}

/**
 * Returns event field values ordered by frequency.
 * @param {Store} VisitorEventsStore
 * @param {RuleSpecV0_1} descriptor
 * @returns {Array.<*>}
 * @private
 */
function getByFrequency(VisitorEventsStore:StoreClass, descriptor:RuleSpecV0_1): any[] {
  descriptor = _.cloneDeep(descriptor) || {};  // Clone because we may set defaults.
  // Assert that descriptor has required pick name and modifier.
  if (!descriptor.pick) {
    throw new Error('No "pick" clause provided in query descriptor');
  }
  if (!descriptor.pick.name) {
    throw new Error('No field name provided in "pick" clause');
  }
  // Default to MOST_FREQUENT.
  descriptor.pick.modifier = descriptor.pick.modifier || rule_builder.FREQUENCY_FILTERS.MOST_FREQUENT;
  var errors = validateDescriptorForAPI(descriptor, _.values(rule_builder.FREQUENCY_FILTERS));
  if (errors) {
    throw new Error(errors.join('\n'));
  }

  var events = eventsHelper.getEvents(VisitorEventsStore);
  return generateAndExecuteRule(events, descriptor);
}

/**
 * Returns multiple events, multiple values, single event, or single value.
 * @param {Store} VisitorEventsStore
 * @param {RuleSpecV0_2} descriptor
 * @returns {*}
 * @throws {Error} if the descriptor does not match the high-level behavioral rule format v0.2.
 * @private
 */
function query(VisitorEventsStore:StoreClass, descriptor:RuleSpecV0_2): any {
  var rule = rule_builder.buildFromSpecV0_2(descriptor);
  var events = eventsHelper.getEvents(VisitorEventsStore);
  var result = eventsToObjects(rules.execute(rule, events), {
    revenueAsTag: true,
    timeAsTimestamp: false,
  });

  if (descriptor.pick || (descriptor.reduce && descriptor.reduce.aggregator === 'count')) {
    // Convert a list of single-value lists into a list of values.
    result = _.flatten(result);
  }

  if (descriptor.reduce) {
    // Convert a list of events/values into a single event/value.
    result = result[0];
  }

  return result;
}

// TODO(tyler.brandt): Make BehaviorApi not require a di.evaluate to use; it's kind of a
// relic of the "old ways" of doing plugins.
// source: https://github.com/optimizely/client-js/pull/754/files#r250993119
module.exports = [
  'stores/visitor_events',
  function ApiFactory(VisitorEventsStore) {
    return {
      /**
       * Returns events based on specifications in descriptor.
       *   - if descriptor is undefined, return all events;
       *   - if descriptor is a number n, return the most recent n events;
       *   - if descriptor is a rule, return result of running rule on all events.
       * @param {Number|RuleSpecV0_1} [descriptor]
       * @returns {Object[]}
       */
      getEvents: _.partial(getEvents, VisitorEventsStore),

      /**
       *
       * Returns event field values ordered by frequency.
       * @param {RuleSpecV0_1} descriptor
       * @returns {Array.<*>}
       */
      getByFrequency: _.partial(getByFrequency, VisitorEventsStore),

      /**
       * Returns multiple events, multiple values, single event, or single value.
       * @param {RuleSpecV0_2} descriptor
       * @returns {*}
       * @throws {Error} if the descriptor does not match the high-level behavioral rule format v0.2.
       */
      query: _.partial(query, VisitorEventsStore),
    };
  },
];
